<script setup lang="ts">
import CompanyHeader from './CompanyHeader.vue'
import ReviewHeader from './ReviewHeader.vue'
import ReviewContent from './ReviewContent.vue'
import RatingDetailsPanel from './RatingDetailsPanel.vue'
import ProsConsSection from './ProsConsSection.vue'

interface Review {
  _id: string
  title: string
  text: string
  overall_rating: number
  created_at: string
  pros?: string
  cons?: string
  rating_details: { [key: string]: number }
  author?: {
    name: string
    role?: string
  }
  company: {
    permalink: string
    logo_url?: string
    brand_name: string
  }
  status: string
  date: Date
  job_title?: string
  soc_code_id?: string
}
const RATING_CRITERIA = {
  compensation_benefits: 'Compensation / Benefits',
  culture: 'Culture',
  management: 'Management',
  work_life_balance: 'Work / Life Balance',
  security_advancement: 'Security / Advancement'
}
const props = defineProps<{
  review: Review
  isEditable?: boolean
  hideAuthor?: boolean
}>()
const formattedDate = computed(() => formatReviewDate(props.review.date))
const emit = defineEmits(['delete', 'edit'])
const showDeleteConfirm = ref(false)
const popularTitle = ref('')
const isLoadingPopularTitle = ref(false)
const handleDelete = () => {
  showDeleteConfirm.value = true
}
const confirmDelete = () => {
  emit('delete', props.review._id)
  showDeleteConfirm.value = false
}
const router = useRouter()
const handleEdit = () => {
  emit('edit', props.review._id)
}
const companyInfo = computed(() => {
  const company = props.review?.company ?? {
    permalink: '',
    logo_url: '/img/logo.svg',
    brand_name: 'Unknown Company'
  }
  return {
    permalink: company?.permalink || '',
    name: company?.brand_name || 'Unknown Company',
    logo_url: company?.logo_url || '/img/logo.svg'
  }
})
const hasCompanyData = computed(() => {
  return true
})
const getCriteriaLabel = (criterion: string | number) => {
  const criterionString = criterion.toString()
  if (RATING_CRITERIA[criterionString]) {
    return RATING_CRITERIA[criterionString]
  }
  return criterionString
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
const fetchPopularTitle = async () => {
  if (!props.review.soc_code_id) {
    popularTitle.value = props.review.job_title || ''
    return
  }
}
const displayJobTitle = computed(() => {
  if (isLoadingPopularTitle.value) return 'Loading...'
  return popularTitle.value || props.review.job_title || ''
})
onMounted(() => {
  fetchPopularTitle()
})
watch(
  () => props.review.soc_code_id,
  (newVal) => {
    if (newVal) {
      fetchPopularTitle()
    } else {
      popularTitle.value = props.review.job_title || ''
    }
  }
)
</script>

<template>
  <Card class="review-card">
    <div class="flex justify-between items-start mb-4 relative">
      <CompanyHeader
        v-if="isEditable"
        :name="companyInfo.name"
        :logo-url="companyInfo.logo_url"
        :permalink="companyInfo.permalink"
        :is-link="true"
      />
      <div v-else />
      <div
        v-if="!isEditable"
        class="text-sm text-gray-500 italic"
      >
        {{ formattedDate }}
      </div>
      <div
        v-if="isEditable"
        class="flex items-center gap-3"
      >
        <span class="text-sm text-gray-500 italic mr-2">{{
          formattedDate
        }}</span>
        <Badge
          :color="review?.status === 'approved' ? 'green' : 'yellow'"
          size="sm"
          class="uppercase font-medium"
        >
          {{ review?.status || "pending" }}
        </Badge>
        <div class="flex gap-2">
          <Button
            color="primary"
            variant="ghost"
            size="xs"
            icon="i-heroicons-pencil-square"
            class="hover:bg-gray-100"
            @click.stop="handleEdit"
          >
            Edit
          </Button>
          <Button
            color="red"
            variant="ghost"
            size="xs"
            icon="i-heroicons-trash"
            class="hover:bg-red-50"
            @click.stop="handleDelete"
          />
        </div>
      </div>
    </div>
    <Modal v-model="showDeleteConfirm">
      <div class="p-4">
        <h3 class="text-lg font-medium mb-2">
          Delete Review
        </h3>
        <p class="text-gray-600 mb-4">
          Are you sure you want to delete this review? This action cannot be
          undone.
        </p>
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            @click="showDeleteConfirm = false"
          >
            Cancel
          </Button>
          <Button
            color="red"
            @click="confirmDelete"
          >
            Delete
          </Button>
        </div>
      </div>
    </Modal>
    <div class="flex flex-col flex-grow">
      <div
        class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4"
      >
        <ReviewHeader :rating="review.overall_rating" />
        <div
          v-if="displayJobTitle"
          class="mt-2 sm:mt-0"
        >
          <Badge
            color="gray"
            variant="subtle"
            size="sm"
            class="text-xs"
          >
            <Icon
              name="i-heroicons-briefcase"
              class="mr-1 w-3 h-3"
            />
            {{ displayJobTitle }}
          </Badge>
        </div>
      </div>
      <div class="flex-grow">
        <ReviewContent
          :title="review.title || ''"
          :text="review.text || ''"
          :line-clamp="false"
        />
        <div
          v-if="
            review.rating_details
              && Object.keys(review.rating_details).length > 0
          "
          class="mt-4"
        >
          <RatingDetailsPanel :rating-details="review.rating_details" />
        </div>
        <div
          v-if="review.pros || review.cons"
          class="mt-4"
        >
          <ProsConsSection
            :pros="review.pros"
            :cons="review.cons"
            :line-clamp="true"
          />
        </div>
      </div>
    </div>
  </Card>
</template>

<style scoped>
.review-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  transition: all 0.3s ease;
}
:deep(.review-content) {
  flex-grow: 1;
}
:deep(.line-clamp) {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
